import { useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { UNSAFE_NavigationContext } from "react-router-dom";

import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

type Navigator = { push: (...args: unknown[]) => void; replace: (...args: unknown[]) => void };

/**
 * Sair do Gerenciamento com rascunho pergunta antes: Salvar ou Descartar tudo.
 *
 * O app usa `BrowserRouter`, sem o `useBlocker` (só existe com o roteador de
 * dados); por isso o `push`/`replace` do navegador do React Router é
 * interceptado enquanto houver mudança, e o Voltar do navegador cai numa entrada
 * repetida desta página antes de sair. Recarregar ou fechar a aba só aceita o
 * aviso nativo do navegador (`beforeunload`), sem botão de Salvar.
 */
export function UnsavedChangesGuard({
  changes,
  saving,
  onSave,
  onDiscard,
}: {
  changes: number;
  saving: boolean;
  onSave: () => Promise<boolean>;
  onDiscard: () => void;
}) {
  const { t } = useTranslation();
  const { navigator } = useContext(UNSAFE_NavigationContext);
  const [pending, setPending] = useState<null | (() => void)>(null);
  const leaving = useRef(false);
  const sentinel = useRef(false);
  const dirty = changes > 0;

  useEffect(() => {
    if (!dirty) return;
    const warn = (event: BeforeUnloadEvent) => {
      if (leaving.current) return;
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  useEffect(() => {
    if (!dirty) return;
    const nav = navigator as unknown as Navigator;
    const push = nav.push;
    const replace = nav.replace;
    nav.push = (...args) => (leaving.current ? push.apply(nav, args) : setPending(() => () => push.apply(nav, args)));
    nav.replace = (...args) => (leaving.current ? replace.apply(nav, args) : setPending(() => () => replace.apply(nav, args)));
    return () => {
      nav.push = push;
      nav.replace = replace;
    };
  }, [dirty, navigator]);

  useEffect(() => {
    if (!dirty) return;
    // Uma entrada repetida desta página: o Voltar para nela, sem desmontar a tela.
    if (!sentinel.current) {
      window.history.pushState(window.history.state, "", window.location.href);
      sentinel.current = true;
    }
    const onPop = () => {
      if (leaving.current) return;
      window.history.pushState(window.history.state, "", window.location.href);
      setPending(() => () => window.history.go(-2));
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [dirty]);

  const proceed = () => {
    const go = pending;
    setPending(null);
    leaving.current = true;
    go?.();
    leaving.current = false;
  };
  const count = changes.toLocaleString("en-US");

  return (
    <AlertDialog open={!!pending} onOpenChange={(open) => { if (!open) setPending(null); }}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t("Unsaved changes")}</AlertDialogTitle>
          <AlertDialogDescription>
            {t("You have {{changes}} unsaved changes. Save them before leaving?", { changes: count })}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <Button variant="outline" onClick={() => { onDiscard(); proceed(); }}>
            {t("Discard all ({{changes}})", { changes: count })}
          </Button>
          <Button variant="action" disabled={saving} onClick={() => { void onSave().then((saved) => { if (saved) proceed(); }); }}>
            {saving ? t("Saving…") : t("Save")}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
